import React, { useCallback } from "react";

import { useToast, UseToastOptions } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { IntlMessageId } from "shared/lang";
import { WarningRoundFilledIcon } from "ui/Icons/Icons";

interface ToastUIOptions extends UseToastOptions {
  title?: IntlMessageId;
  description?: IntlMessageId | React.ReactNode;
}

export const VARIANT_INFO_SUCCESS = "info_success";

export function useCustomToast() {
  const toast = useToast();
  const intl = useIntl();

  const showToastUI = useCallback(
    ({
      id,
      title,
      description,
      containerStyle = {
        alignItems: "center",
      },
      status = "error",
      duration = 4000,
      variant,
      ...rest
    }: ToastUIOptions) => {
      const modifiedID = id || crypto.randomUUID();
      const formattedTitle = title
        ? intl.formatMessage({
            id: title,
          })
        : null;

      let formattedDescription;
      if (React.isValidElement(description)) {
        formattedDescription = description;
      } else {
        formattedDescription =
          description && typeof description === "string"
            ? intl.formatMessage({
                id: description as IntlMessageId,
              })
            : null;
      }
      if (toast.isActive && toast.isActive(modifiedID)) {
        return;
      }

      const icon =
        variant === VARIANT_INFO_SUCCESS ? (
          <WarningRoundFilledIcon color="white" boxSize="1.5rem" />
        ) : (
          rest.icon
        );

      toast({
        id: modifiedID,
        title: formattedTitle,
        status,
        description: formattedDescription,
        containerStyle,
        duration,
        icon,
        variant,
        isClosable: true,
        ...rest,
      });
      // eslint-disable-next-line react-compiler/react-compiler
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [intl, toast],
  );

  const closeToast = useCallback(
    (errorToastId: string) => {
      if (toast.isActive(errorToastId)) {
        toast.close(errorToastId);
      }
    },
    [toast],
  );

  return { showToastUI, closeToast };
}
